import {AbstractUrlService} from 'core/abstract/AbstractUrlService';
import {useHistoryWithGSSP} from 'core/next/hooks/useHistotyWithGSSP';
import {usePageLoading} from 'core/next/hooks/usePageLoading';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {ICategoryNode} from 'modules/categories/models/CategoryTree';
import {ProductListPageContext} from 'modules/products/context';
import {setIsSelection} from 'modules/products/helpers';
import {useUpdatedFilterList} from 'modules/products/hooks/useUpdatedFilterList';
import {ProductListPage} from 'modules/products/pages/ProductListPage';
import {
    selectAppliedChooseFilterByVariants,
    selectAppliedRangeFilterList,
    selectComboFilterList,
    selectFilterList,
    selectIsFilterPanelVisible,
    selectIsListingLoading,
    selectIsListingPartiallyLoading,
    selectList,
    selectListType,
    selectPage,
    selectPageCount,
    selectPageSize,
    selectPageSizeList,
    selectPaginationType,
    selectSortingList,
} from 'modules/products/selectors';
import {productsSlice} from 'modules/products/slice';
import {getProductList} from 'modules/products/thunks';
import {FilterChoose, ProductShort} from 'new-models';
import {useRouter} from 'next/router';
import {SliceProvider} from 'plugins/context/Slice/SliceProvider';
import {useFilterUrlService} from 'plugins/modules/filter/hooks/useFilterUrlService';
import {FilterProvider} from 'plugins/modules/filter/provider';
import {FilterUrlService} from 'plugins/modules/filter/services/FilterUrlService';
import {TFilter} from 'plugins/modules/filter/types';
import {ListingProvider} from 'plugins/modules/listing/provider';
import {usePaginationUrlService} from 'plugins/modules/pagination/hooks/usePaginationUrlService';
import {PaginationProvider} from 'plugins/modules/pagination/provider';
import {useSortUrlService} from 'plugins/modules/sorting/hooks/useSortUrlService';
import {SortingProvider} from 'plugins/modules/sorting/provider';
import React, {FC, ReactNode, useCallback, useRef} from 'react';
import {COLLECTION_X_PATH} from 'routing/constants';

interface IProps {
    category?: ICategoryNode;
    ComponentHeader?: ReactNode;
    MobileHeaderComponent?: ReactNode;
    noMobileHeaderOffset?: boolean;
    withBanners?: boolean;
    filterExceptions?: TFilter['code'][];
}

export const ProductListPageProvider: FC<IProps> = ({
    category,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ComponentHeader,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    MobileHeaderComponent,
    noMobileHeaderOffset,
    withBanners = true,
    filterExceptions = [],
}) => {
    useHistoryWithGSSP();
    const router = useRouter();

    const dispatch = useAppDispatch();
    const isPageLoadingShallow = usePageLoading({
        watchShallow: true,
    });

    const productList = useAppSelector(selectList<ProductShort>);
    const listType = useAppSelector(selectListType);

    const count = useAppSelector(selectPageCount);
    const page = useAppSelector(selectPage);
    const pageSize = useAppSelector(selectPageSize);
    const paginationType = useAppSelector(selectPaginationType);

    const filterList = useAppSelector(selectFilterList);
    const isFilterPanelVisible = useAppSelector(selectIsFilterPanelVisible);
    const appliedChooseFiltersByVariantList = useAppSelector(selectAppliedChooseFilterByVariants);
    const comboFiltersList = useAppSelector(selectComboFilterList);
    const appliedRangeFilters = useAppSelector(selectAppliedRangeFilterList);

    const sortingList = useAppSelector(selectSortingList);
    const pageSizeList = useAppSelector(selectPageSizeList);

    const isProductSegmentListLoading = useAppSelector(selectIsListingPartiallyLoading);
    const isProductListLoading = useAppSelector(selectIsListingLoading);

    const urlServiceCallback = useCallback<AbstractUrlService['onParamsChange']>(
        (searchQuery) => {
            if (router.pathname === COLLECTION_X_PATH) {
                searchQuery.set('filter[collection]', router.query.path as string);
            }

            if (setIsSelection(router.query.path)) {
                searchQuery.set('filter[nameplates]', router.query.path as string);
            }

            dispatch(getProductList(searchQuery, category?.id)).then(({filters}) =>
                FilterUrlService.setFilterQueryParams(filters)
            );
        },
        [category?.id, dispatch, router.pathname, router.query.path]
    );

    const queryRef = useRef<URLSearchParams>(new URLSearchParams());

    const filterUrlService = useFilterUrlService(queryRef.current, urlServiceCallback);
    const sortUrlService = useSortUrlService(queryRef.current, urlServiceCallback);
    const paginationUrlService = usePaginationUrlService(queryRef.current, urlServiceCallback);
    const updatedFilterList = useUpdatedFilterList(filterList, filterExceptions);
    const updatedAppliedChooseFilterList = useUpdatedFilterList(appliedChooseFiltersByVariantList, filterExceptions);

    const isMountListingPage = 1 < Object.keys(router.query).length || Boolean(productList.length);

    return (
        <SliceProvider slice={productsSlice}>
            <ProductListPageContext.Provider
                value={{
                    category,
                }}
            >
                <ListingProvider
                    isListLoading={isProductListLoading || isPageLoadingShallow}
                    isListPartiallyLoading={isProductSegmentListLoading}
                    isShowListingBanner
                    list={productList}
                    listType={listType}
                >
                    <SortingProvider sortingList={sortingList} urlService={sortUrlService}>
                        <FilterProvider
                            appliedChooseFiltersByVariantList={updatedAppliedChooseFilterList as FilterChoose[]}
                            appliedRangeFiltersList={appliedRangeFilters}
                            comboFilterList={comboFiltersList}
                            filterList={updatedFilterList}
                            isFilterPanelVisible={isFilterPanelVisible}
                            urlService={filterUrlService}
                        >
                            <PaginationProvider
                                count={count}
                                page={page}
                                pageSize={pageSize}
                                pageSizeList={pageSizeList}
                                type={paginationType}
                                urlService={paginationUrlService}
                            >
                                {ComponentHeader}
                                {isMountListingPage && (
                                    <ProductListPage
                                        MobileHeaderComponent={MobileHeaderComponent}
                                        noMobileHeaderOffset={noMobileHeaderOffset}
                                        withBanners={withBanners}
                                    />
                                )}
                            </PaginationProvider>
                        </FilterProvider>
                    </SortingProvider>
                </ListingProvider>
            </ProductListPageContext.Provider>
        </SliceProvider>
    );
};
